package games.polyclub.website.frontend.store.pages.logged

import games.polyclub.frontend.lib.react.bootstrap.components.BAlert
import games.polyclub.frontend.lib.react.bootstrap.components.Card
import games.polyclub.frontend.lib.react.bootstrap.css.utility.BootstrapColor
import games.polyclub.frontend.lib.react.simplified.SFC
import games.polyclub.frontend.lib.react.simplified.tags.*
import games.polyclub.frontend.lib.react.utils.classSet
import games.polyclub.frontend.lib.react.utils.useEffectAsync
import games.polyclub.frontend.store.shared.CreateOrderResult
import games.polyclub.message.unaryMinus
import games.polyclub.service.payment.api.ProductReceiver
import games.polyclub.service.payment.api.PurchaseType
import games.polyclub.service.payment.api.id.Gateway
import games.polyclub.service.payment.api.id.OrderId
import games.polyclub.service.player.id.api.PlayerId
import games.polyclub.service.player.id.api.SystemPlayerId
import games.polyclub.tool.functional.Either
import games.polyclub.tool.functional.Option
import games.polyclub.tool.functional.compute
import games.polyclub.tool.functional.retrying
import games.polyclub.tool.math.Decimal
import games.polyclub.tool.math.Decimal.Companion.dec
import games.polyclub.tool.strings.Currency
import games.polyclub.website.frontend.store.StoreBff
import games.polyclub.website.frontend.store.appScope
import games.polyclub.website.frontend.store.components.AsyncImg
import games.polyclub.website.frontend.store.components.Loading
import games.polyclub.website.frontend.store.context.addBreadcrumb
import games.polyclub.website.frontend.store.context.useCart
import games.polyclub.website.frontend.store.hooks.*
import games.polyclub.website.frontend.store.layout.userarea.UserAreaLayout
import games.polyclub.website.frontend.store.pages.RegisterLocationState
import games.polyclub.website.frontend.store.sitemap.LoggedSitemap
import games.polyclub.website.frontend.store.sitemap.PublicSitemap
import js.core.jso
import kotlinx.collections.immutable.persistentListOf
import kotlinx.coroutines.launch
import react.Props
import react.router.dom.Link
import react.router.useLocation
import react.router.useNavigate
import react.useCallback
import react.useEffect
import react.useState
import web.cssom.pct
import web.html.ButtonType
import web.html.InputType
import web.window.WindowTarget

val CartPage = SFC<Props> { _ ->

    val cartCtx = useCart()
    val authCtx = useAuth()

    val location = useLocation()
    val navigate = useNavigate()

    val (response, setResponse) = useState<Option<CreateOrderResult>?>(null)
    val (giftConfirmations, setGiftConfirmations) = useState(emptySet<PlayerId>())
    val (giftTermsAccepted, setGiftTermsAccepted) = useState(false)
    val (giftRefundTermsAccepted, setGiftRefundTermsAccepted) = useState(false)
    val (giveawayConfirmed, setGiveawayConfirmed) = useState(false)
    val (giveawayTermsAccepted, setGiveawayTermsAccepted) = useState(false)
    val (giveawayRefundTermsAccepted, setGiveawayRefundTermsAccepted) = useState(false)
    val (selectingGateway, setSelectingGateway) = useState(false)
    val (submission, setSubmission) = useState<Option<Either<OrderId, String>>?>(null)
    val (gateway, setGateway) = useState<Gateway?>(null)

    val submit = useCallback(setSubmission, cartCtx, gateway) {
        if (gateway == null) {
            return@useCallback
        }
        setSubmission(Option.empty())
        val cart = cartCtx.cart
        appScope.launch {
            retrying {
                setSubmission(Option.of(StoreBff.createInvoice(cart, gateway)))
            }
        }
    }

    val selectedProducts = cartCtx.selectedProducts
    appScope.useEffectAsync(authCtx.auth, selectedProducts, setResponse) {
        setResponse(null)
        if (selectedProducts.isEmpty()) {
            setResponse(Option.empty())
        } else {
            setResponse(Option.of(retrying {
                StoreBff.createOrder(cartCtx.cart)
            }).filter { it.order?.products?.isEmpty() != true }.also { opt ->
                opt.map { it.order }.ifPresent { order ->
                    logEvent(
                        category = GACategory.CART,
                        action = GAAction.VIEW_CART,
                        params = order.toGA4()
                    )
                }
            })
        }
    }

    useEffect(submission?.getOrNull()?.a) {
        submission?.getOrNull()?.ifA { orderId ->
            logEvent(
                action = GAAction.BEGIN_CHECKOUT,
                category = GACategory.CART,
                params = response?.getOrNull()?.order?.toGA4()
            )
            cartCtx.setProductList(persistentListOf())
            navigate(LoggedSitemap.PAY_ROOT.path + "/$orderId")
        }
    }

    useEffect(selectingGateway, response) {
        val responseObj = response?.getOrNull() ?: return@useEffect
        if (selectingGateway && responseObj.currency != Currency.BRL) {
            setGateway(Gateway.PAYPAL_INVOICE)
            submit()
        }
    }

    useEffect(response?.getOrNull()?.noPaymentProfile) {
        if (response?.getOrNull()?.noPaymentProfile == true) {
            navigate(LoggedSitemap.REGISTER_PAYMENT_PROFILE.path, jso {
                state = jso<RegisterLocationState> {
                    returnLocation = location.pathname
                    returnState = location.state
                }
            })
        }
    }

    UserAreaLayout {
        addBreadcrumb(LoggedSitemap.CART.pathPart, -"Carrinho")
        pageTitle = -"Carrinho"
        pageTitleIcon = LoggedSitemap.CART.icon
        if (response == null) {
            div("card-body") {
                Loading()
            }
            return@UserAreaLayout
        }
        if (submission != null && submission.isEmpty) {
            div("card-body") {
                Loading()
            }
            return@UserAreaLayout
        }
        Card {
            submission?.getOrNull()?.b?.let { errorMessage ->
                BAlert {
                    onClose = {
                        setSubmission(null)
                    }
                    color = BootstrapColor.DANGER
                    +errorMessage
                }
            }
            if (response.isEmpty) {
                p {
                    +"Seu carrinho está vazio, que tal adicionar algo legal nele?"
                }
                return@Card
            }
            val order = response.get().order
            if (order == null || response.get().noPaymentProfile) {
                p {
                    +"Você precisa preencher um formulário antes, por favor aguarde..."
                }
                return@Card
            }

            if (selectingGateway) {
                val validGateways = if (order.currency == Currency.BRL) {
                    //listOf(Gateway.PAG_SEGURO, Gateway.PICPAY)
                    listOf(Gateway.PAYPAL_INVOICE)
                } else {
                    listOf(Gateway.PAYPAL_INVOICE)
                }
                //title = "Selecione o Método de Pagamento"
                div("row mb-5") {
                    div("col text-center") {
                        +"Por favor, escolha o método de pagamento de sua preferência."
                    }
                }
                div("row mb-5") {
                    div("col text-center") {
                        div("btn-group") {
                            validGateways.forEach { validGateway ->
                                button("btn btn-${if (gateway == validGateway) "primary" else "default"}") {
                                    type = ButtonType.button
                                    onClick = {
                                        setGateway(validGateway)
                                    }
                                    when (validGateway) {
                                        Gateway.PAG_SEGURO, Gateway.NUBANK -> {
                                            img("mr-2 mb-3") {
                                                height = 32.0
                                                src = "/img/logo-pix.svg"
                                                alt = ""
                                            }
                                        }

                                        Gateway.PICPAY -> {
                                            img("mb-3") {
                                                height = 32.0
                                                src = "/img/logo-picpay.png"
                                                alt = ""
                                            }
                                        }

                                        Gateway.PAYPAL, Gateway.PAYPAL_INVOICE -> {
                                            img("mb-3") {
                                                height = 32.0
                                                src = "/img/logo-paypal.png"
                                                alt = ""
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                div("row") {
                    div("col text-center") {
                        button("btn btn-outline-danger mr-2") {
                            +"Voltar"
                            onClick = {
                                setSelectingGateway(false)
                            }
                        }

                        button("btn btn-primary") {
                            +"Realizar Pagamento"
                            disabled = gateway == null
                            onClick = {
                                setSelectingGateway(false)
                                submit()
                            }
                        }
                    }
                }
                return@Card
            }

            var readyToSubmit = order.products.isNotEmpty()
            var giftCount = 0.dec
            var giveawayCount = 0.dec
            val giftsTo = mutableMapOf<ProductReceiver, Decimal>()
            table("table table-striped") {
                thead {
                    tr {
                        th {
                            +"Quantidade"
                        }
                        th {
                            +"Produto"
                        }
                        th {
                            +"Para"
                        }
                        th {
                            +"Valor Unitário"
                        }
                        th {
                            +"Taxas"
                        }
                        th {
                            +"Descontos"
                        }
                        th {
                            +"Subtotal"
                        }
                        th {
                            +" "
                        }
                    }
                }
                tbody {
                    val currency = order.currency
                    order.products.forEach { item ->
                        tr {
                            td {
                                div("row") {
                                    div("col-3") {
                                        input("form-control border-0") {
                                            item.available?.let { available ->
                                                if (item.quantity > available.dec) {
                                                    classSet += "is-invalid"
                                                }
                                            }
                                            type = InputType.number
                                            value = item.quantity.toString()
                                            step = 1.0
                                            onChange = { ev ->
                                                val value = ev.target.valueAsNumber
                                                val products = cartCtx.selectedProducts
                                                val index = products.indexOfFirst { product ->
                                                    product.offerId == item.sourceOfferId &&
                                                            product.quantity == item.sourceOfferQuantity &&
                                                            product.giftTo == when (item.purchaseType) {
                                                        PurchaseType.NORMAL -> null
                                                        PurchaseType.GIFT -> item.to.playerId
                                                        PurchaseType.GIVEAWAY -> SystemPlayerId.GIVEAWAY
                                                    }
                                                }
                                                if (index >= 0) {
                                                    val current = products[index]
                                                    cartCtx.setProductList(
                                                        products.set(
                                                            index,
                                                            current.copy(quantity = value.toInt())
                                                        )
                                                    )
                                                }
                                            }
                                        }
                                    }
                                    div("col m-auto") {
                                        item.available?.let { available ->
                                            +" ($available disponíve${if (available == 1) "l" else "is"})"
                                        }
                                    }
                                }
                            }
                            td {
                                +item.productName
                            }
                            td {
                                when (item.purchaseType) {
                                    PurchaseType.GIVEAWAY -> strong {
                                        giveawayCount += item.quantity
                                        +(if (item.quantity == Decimal.ONE) "Ser sorteado" else "Serem sorteados")
                                    }

                                    PurchaseType.GIFT -> strong {
                                        giftCount += item.quantity
                                        giftsTo.compute(item.to) { _, current ->
                                            (current ?: 0.dec) + item.quantity
                                        }
                                        AsyncImg("img-circle elevation-2 bg-secondary") {
                                            srcLoader = { StoreBff.avatarUrl(item.to.playerId.networkId) }
                                            width = 32.0
                                            height = 32.0
                                            alt = ""
                                        }
                                        +" ${item.to.playerName} "
                                        em {
                                            +"(Presente)"
                                        }
                                    }

                                    PurchaseType.NORMAL -> {
                                        AsyncImg("img-circle elevation-2 bg-secondary") {
                                            srcLoader = { StoreBff.avatarUrl(item.to.playerId.networkId) }
                                            width = 32.0
                                            height = 32.0
                                            alt = ""
                                        }
                                        +" ${item.to.playerName} "
                                    }
                                }
                            }
                            td {
                                +item.basePrince.toString()
                            }
                            td {
                                if (item.taxes.none { it.affectsCustomer }) {
                                    +currency.amount(0).toString()
                                } else {
                                    dl {
                                        item.taxes.asSequence().filter { it.affectsCustomer }.forEach { tax ->
                                            dt {
                                                +tax.name
                                            }
                                            dd {
                                                var increase = tax.increase
                                                if (tax.perAmount) {
                                                    increase *= item.quantity
                                                }
                                                +increase.toString()
                                            }
                                        }
                                    }
                                }
                            }
                            td {
                                if (item.discounts.none { it.affectsCustomer }) {
                                    +currency.amount(0).toString()
                                } else {
                                    dl {
                                        item.discounts.asSequence().filter { it.affectsCustomer }.forEach { discount ->
                                            dt {
                                                +discount.name
                                            }
                                            dd {
                                                var reduction = discount.reduction
                                                if (discount.perAmount) {
                                                    reduction *= item.quantity
                                                }
                                                +reduction.toString()
                                            }
                                        }
                                    }
                                }
                            }
                            td {
                                +item.total.toString()
                            }
                            td {
                                button("btn btn-outline-danger btn-sm border-0") {
                                    onClick = {
                                        val products = cartCtx.selectedProducts
                                        val index = products.indexOfFirst { product ->
                                            product.offerId == item.sourceOfferId &&
                                                    product.quantity == item.sourceOfferQuantity &&
                                                    product.giftTo == when (item.purchaseType) {
                                                PurchaseType.NORMAL -> null
                                                PurchaseType.GIFT -> item.to.playerId
                                                PurchaseType.GIVEAWAY -> SystemPlayerId.GIVEAWAY
                                            }
                                        }
                                        if (index >= 0) {
                                            cartCtx.setProductList(products.removeAt(index))
                                        }
                                    }
                                    i("fa fa-trash")
                                }
                            }
                        }
                    }
                }
            }
            if (giveawayCount > 0.dec || giftCount > 0.dec) {
                Card("bg-warning") {
                    title = "Confirmações e Termos Adicionais"
                    if (giftCount > 0.dec) {
                        div("form-group") {
                            div("form-check") {
                                input("form-check-input") {
                                    type = InputType.checkbox
                                    if (!giftTermsAccepted) {
                                        readyToSubmit = false
                                    }
                                    checked = giftTermsAccepted
                                    onChange = { ev ->
                                        setGiftTermsAccepted(ev.target.checked)
                                    }
                                }
                                label("form-check-label") {
                                    +"Li e aceito os "
                                    Link {
                                        to = PublicSitemap.GIFT_TERMS.path
                                        target = WindowTarget._blank
                                        +"Termos e Condições para Compras de Presentes"
                                    }
                                }
                            }
                        }
                        giftsTo.forEach { (nameId, quantity) ->
                            div("form-group") {
                                div("form-check") {
                                    input("form-check-input") {
                                        type = InputType.checkbox
                                        val confirmed = nameId.playerId in giftConfirmations
                                        if (!confirmed) {
                                            readyToSubmit = false
                                        }
                                        checked = confirmed
                                        onChange = { ev ->
                                            val nowConfirmed = ev.target.checked
                                            setGiftConfirmations { current ->
                                                if (!nowConfirmed) {
                                                    current - nameId.playerId
                                                } else {
                                                    current + nameId.playerId
                                                }
                                            }
                                        }
                                    }
                                    label("form-check-label") {
                                        +"Confirmo estar comprando $quantity produto${if (quantity == 1.dec) "" else "s"} de presente para "
                                        strong {
                                            +nameId.playerName
                                        }
                                    }
                                }
                            }
                        }
                        div("form-group") {
                            div("form-check") {
                                input("form-check-input") {
                                    type = InputType.checkbox
                                    if (!giftRefundTermsAccepted) {
                                        readyToSubmit = false
                                    }
                                    checked = giftRefundTermsAccepted
                                    onChange = { ev ->
                                        setGiftRefundTermsAccepted(ev.target.checked)
                                    }
                                }
                                label("form-check-label") {
                                    +"Entendo que compras de produtos para presente não podem ser canceladas após algum produto ser consumido ou transferido da conta das pessoas presenteadas."
                                }
                            }
                        }
                    }
                    if (giveawayCount > 0.dec) {
                        div("form-group") {
                            div("form-check") {
                                input("form-check-input") {
                                    type = InputType.checkbox
                                    if (!giveawayTermsAccepted) {
                                        readyToSubmit = false
                                    }
                                    checked = giveawayTermsAccepted
                                    onChange = { ev ->
                                        setGiveawayTermsAccepted(ev.target.checked)
                                    }
                                }
                                label("form-check-label") {
                                    +"Li e aceito os "
                                    Link {
                                        to = PublicSitemap.GIVEAWAY_PURCHASE_TERMS.path
                                        target = WindowTarget._blank
                                        +"Termos e Condições para Compras de Produtos para Sorteio"
                                    }
                                }
                            }
                        }
                        div("form-group") {
                            div("form-check") {
                                input("form-check-input") {
                                    type = InputType.checkbox
                                    if (!giveawayConfirmed) {
                                        readyToSubmit = false
                                    }
                                    checked = giveawayConfirmed
                                    onChange = { ev ->
                                        setGiveawayConfirmed(ev.target.checked)
                                    }
                                }
                                label("form-check-label") {
                                    +"Estou ciente que estou comprando $giveawayCount produto${if (giveawayCount == 1.dec) "" else "s"} para "
                                    strong {
                                        +"sorteio"
                                    }
                                }
                            }
                        }
                        div("form-group") {
                            div("form-check") {
                                input("form-check-input") {
                                    type = InputType.checkbox
                                    if (!giveawayRefundTermsAccepted) {
                                        readyToSubmit = false
                                    }
                                    checked = giveawayRefundTermsAccepted
                                    onChange = { ev ->
                                        setGiveawayRefundTermsAccepted(ev.target.checked)
                                    }
                                }
                                label("form-check-label") {
                                    +"Entendo que compras de produtos para sorteio não podem ser canceladas após algum produto ser sorteado."
                                }
                            }
                        }
                    }
                }
            }

            div("row") {
                div("col-6") {
                    p("lead") {
                        +"Métodos de Pagamento"
                    }
                    div("row") {
                        div("col") {
                            if (order.currency == Currency.BRL) {
                                /*img("mr-2 mb-3") {
                                    height = 32.0
                                    src = "/img/logo-pix.svg"
                                    alt = ""
                                }
                                img("mb-3") {
                                    height = 32.0
                                    src = "/img/logo-picpay.png"
                                    alt = ""
                                }*/
                                img("mb-3") {
                                    height = 32.0
                                    src = "/img/logo-paypal.png"
                                    alt = ""
                                }
                            } else {
                                img("mb-3") {
                                    height = 32.0
                                    src = "/img/logo-paypal.png"
                                    alt = ""
                                }
                            }
                        }
                    }
                    div("row") {
                        div("col") {
                            if (order.currency == Currency.BRL) {
                                p {
                                    +"Como você mora no Brasil, somente transações em Reais são permitidas."
                                }
                                p {
                                    +"No Brasil aceitamos Pix ou os métodos fornecidos pelo PicPay."
                                }
                                p{
                                    strong {
                                        +"Nota: "
                                    }
                                    +"Estamos temporariamente aceitando apenas os métodos fornecidos PayPal, inclusive para brasileiros."
                                }
                            } else {
                                p {
                                    +"Como você mora fora do Brasil, somente transações em Euro são permitidas."
                                }
                                p {
                                    +"Fora do Brasil aceitamos apenas os métodos fornecidos pelo PayPal."
                                }
                            }
                        }
                    }
                }
                div("col-6") {
                    p("lead") {
                        +"Totais"
                    }
                    div("table-responsive") {
                        table("table") {
                            tbody {
                                tr {
                                    th {
                                        style = jso {
                                            width = 50.pct
                                        }
                                        +"Subtotal:"
                                    }
                                    td {
                                        +order.fullBasePrice.toString()
                                    }
                                }
                                tr {
                                    th {
                                        +"Taxas:"
                                    }
                                    td {
                                        +order.totalTax.toString()
                                    }
                                }
                                tr {
                                    th {
                                        +"Descontos:"
                                    }
                                    td {
                                        +order.totalDiscount.toString()
                                    }
                                }
                                tr {
                                    th {
                                        +"Total:"
                                    }
                                    td {
                                        +order.total.toString()
                                    }
                                }
                            }
                        }
                    }
                }
            }

            div("row") {
                div("col text-right") {
                    button("btn btn-outline-danger mr-2") {
                        +"Limpar Carrinho"
                        disabled = order.products.isEmpty()
                        onClick = {

                            cartCtx.setProductList(persistentListOf())
                        }
                    }

                    button("btn btn-primary") {
                        +"Prosseguir para Pagamento"
                        disabled = !readyToSubmit
                        onClick = {
                            setSelectingGateway(true)
                        }
                    }
                }
            }
        }
    }
}
